import { useState } from 'react';
import { ActionIcon, Badge, Box, Center, Checkbox, Group, Stack, Table, Text } from '@mantine/core';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { Button } from 'src/design-system/Button';
import { Tooltip } from 'src/design-system/Tooltip';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import PageLoader from 'src/UI/loader/PageLoader';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import type { CalendarEvent } from 'src/meetings/models';
import { formatDueDate } from 'src/meetings/utils/date-utils';
import {
  EMPTY_DRAFT,
  MeetingActionForm,
  type ActionDraft,
} from 'src/meetings/detail/tabs/MeetingActionForm';
import {
  useCreateMeetingAction,
  useDeleteMeetingAction,
  useMeetingActions,
  useUpdateMeetingAction,
  type MeetingAction,
} from 'src/meetings/hooks/useMeetingActions';

type Props = {
  event: CalendarEvent;
};

const LOADER_MIN_HEIGHT = 300;
const TABLE_MIN_WIDTH = 1080;
const COMPLETED_ROW_OPACITY = 0.55;
const COLUMN_WIDTH = { checkbox: 44, dueDate: 140, status: 120, source: 170, actions: 84 } as const;

const HeaderCell = ({ children }: { children?: React.ReactNode }) => (
  <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
    {children}
  </Text>
);

const StatusBadge = ({ action }: { action: MeetingAction }) => {
  if (action.status === 'COMPLETED') {
    return <Badge variant="green">Complete</Badge>;
  }
  if (action.isOverdue) {
    return (
      <Tooltip label="This was due before today" withArrow>
        <Badge variant="warning" style={{ cursor: 'default' }}>
          Overdue
        </Badge>
      </Tooltip>
    );
  }
  return <Badge variant="grey">To do</Badge>;
};

const SourceBadge = ({ action }: { action: MeetingAction }) => {
  if (action.isUserAdded) {
    return <Badge variant="brand">Added manually</Badge>;
  }
  if (action.isUserEdited) {
    return <Badge variant="warning">Edited</Badge>;
  }
  return <Badge variant="grey">From meeting note</Badge>;
};

export const ActionsTab = ({ event }: Props) => {
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [draft, setDraft] = useState<ActionDraft>(EMPTY_DRAFT);
  const [newAction, setNewAction] = useState<ActionDraft>(EMPTY_DRAFT);
  const [isAdding, setIsAdding] = useState(false);
  const [actionToRemove, setActionToRemove] = useState<MeetingAction | undefined>(undefined);

  const { actions, hasMeetingNote, isLoading, isError } = useMeetingActions(event.id, {
    enabled: !!event.id,
  });
  const { mutateAsync: createAction, isPending: isCreating } = useCreateMeetingAction(event.id);
  const { mutateAsync: updateAction } = useUpdateMeetingAction(event.id);
  const { mutateAsync: deleteAction, isPending: isRemoving } = useDeleteMeetingAction(event.id);

  const handleToggleComplete = async (action: MeetingAction) => {
    try {
      await updateAction({
        id: action.id,
        values: { completed: action.status !== 'COMPLETED' },
      });
    } catch (error) {
      notifyAndLogError(
        'Action not updated',
        'We could not update this action. Please try again.',
        error,
      );
    }
  };

  const handleStartEditing = (action: MeetingAction) => {
    setEditingId(action.id);
    setDraft({
      description: action.description,
      owner: action.owner ?? '',
      dueDate: action.dueDate ?? null,
      notes: action.notes ?? '',
    });
  };

  const handleSaveEdit = async (action: MeetingAction) => {
    const description = draft.description.trim();
    if (!description) {
      return;
    }
    try {
      await updateAction({
        id: action.id,
        values: {
          description,
          owner: draft.owner.trim() || null,
          dueDate: draft.dueDate ?? null,
          notes: draft.notes.trim() || null,
        },
      });
      setEditingId(undefined);
    } catch (error) {
      notifyAndLogError(
        'Action not saved',
        'We could not save this action. Please try again.',
        error,
      );
    }
  };

  const handleConfirmRemove = async () => {
    if (!actionToRemove) {
      return;
    }
    try {
      await deleteAction(actionToRemove.id);
      setActionToRemove(undefined);
    } catch (error) {
      notifyAndLogError(
        'Action not removed',
        'We could not remove this action. Please try again.',
        error,
      );
    }
  };

  const handleAdd = async () => {
    const description = newAction.description.trim();
    if (!description) {
      return;
    }
    try {
      await createAction({
        description,
        owner: newAction.owner.trim() || undefined,
        dueDate: newAction.dueDate ?? undefined,
        notes: newAction.notes.trim() || undefined,
      });
      setNewAction(EMPTY_DRAFT);
      setIsAdding(false);
    } catch (error) {
      notifyAndLogError(
        'Action not added',
        'We could not add this action. Please try again.',
        error,
      );
    }
  };

  if (!event.hasTranscription) {
    return (
      <Box p="md">
        <Text size="textSm" c="gray.7">
          There is no recording for this meeting.
        </Text>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box style={{ position: 'relative', minHeight: LOADER_MIN_HEIGHT }}>
        <PageLoader absolute={false} />
        <Center mt="xl">
          <Text size="textSm" c="gray.7" fw={FONT_WEIGHTS.semibold}>
            Reading the actions from the meeting note...
          </Text>
        </Center>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p="md">
        <Text size="textSm" c="gray.7">
          We could not load the actions for this meeting. Please try again later.
        </Text>
      </Box>
    );
  }

  const rows = actions ?? [];
  const completedCount = rows.filter(action => action.status === 'COMPLETED').length;
  const emptyMessage = hasMeetingNote
    ? 'No actions were agreed in this meeting.'
    : 'Actions appear once the meeting note has been generated.';

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Text size="textSm" c="gray.7">
          {rows.length === 0
            ? emptyMessage
            : `${rows.length} ${rows.length === 1 ? 'action' : 'actions'} · ${completedCount} completed`}
        </Text>
        {!isAdding && (
          <Button
            variant="primary"
            size="sm"
            leftSection={<IconPlus size={16} />}
            onClick={() => setIsAdding(true)}
          >
            Add action
          </Button>
        )}
      </Group>

      {isAdding && (
        <MeetingActionForm
          title="New action"
          value={newAction}
          submitLabel="Add action"
          isSubmitting={isCreating}
          onChange={setNewAction}
          onSubmit={handleAdd}
          onCancel={() => {
            setNewAction(EMPTY_DRAFT);
            setIsAdding(false);
          }}
        />
      )}

      {rows.length > 0 && (
        <Table.ScrollContainer minWidth={TABLE_MIN_WIDTH}>
          <Table variant="bare" verticalSpacing="sm" layout="fixed">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={COLUMN_WIDTH.checkbox} />
                <Table.Th>
                  <HeaderCell>Action</HeaderCell>
                </Table.Th>
                <Table.Th w="18%">
                  <HeaderCell>Owner</HeaderCell>
                </Table.Th>
                <Table.Th w={COLUMN_WIDTH.dueDate}>
                  <HeaderCell>Due date</HeaderCell>
                </Table.Th>
                <Table.Th w="22%">
                  <HeaderCell>Notes</HeaderCell>
                </Table.Th>
                <Table.Th w={COLUMN_WIDTH.status}>
                  <HeaderCell>Status</HeaderCell>
                </Table.Th>
                <Table.Th w={COLUMN_WIDTH.source}>
                  <HeaderCell>Source</HeaderCell>
                </Table.Th>
                <Table.Th w={COLUMN_WIDTH.actions} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(action => {
                const isComplete = action.status === 'COMPLETED';
                const isEditing = editingId === action.id;
                return (
                  <Table.Tr
                    key={action.id}
                    style={{ opacity: isComplete ? COMPLETED_ROW_OPACITY : 1 }}
                  >
                    {isEditing ? (
                      <Table.Td colSpan={8}>
                        <MeetingActionForm
                          title="Edit action"
                          value={draft}
                          submitLabel="Save"
                          onChange={setDraft}
                          onSubmit={() => handleSaveEdit(action)}
                          onCancel={() => setEditingId(undefined)}
                        />
                      </Table.Td>
                    ) : (
                      <>
                        <Table.Td>
                          <Checkbox
                            checked={isComplete}
                            aria-label={`Mark "${action.description}" as complete`}
                            onChange={() => handleToggleComplete(action)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                            {action.description}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="textSm" c={action.owner ? 'gray.8' : 'gray.5'}>
                            {action.owner ?? 'Not stated'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="textSm" c={action.dueDate ? 'gray.8' : 'gray.5'}>
                            {formatDueDate(action.dueDate)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="textSm" c={action.notes ? 'gray.8' : 'gray.5'}>
                            {action.notes || 'No notes'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge action={action} />
                        </Table.Td>
                        <Table.Td>
                          <SourceBadge action={action} />
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xxxs" justify="flex-end" wrap="nowrap">
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label="Edit action"
                              onClick={() => handleStartEditing(action)}
                            >
                              <IconPencil size={16} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label="Remove action"
                              onClick={() => setActionToRemove(action)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </>
                    )}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <ConfirmDeleteModal
        opened={!!actionToRemove}
        onClose={() => setActionToRemove(undefined)}
        onConfirm={handleConfirmRemove}
        title={actionToRemove?.isUserAdded ? 'Delete action' : 'Remove action'}
        message={
          actionToRemove?.isUserAdded
            ? `"${actionToRemove.description}" will be deleted.`
            : `"${actionToRemove?.description}" will be removed from this list. The meeting note itself is not changed.`
        }
        confirmLabel={actionToRemove?.isUserAdded ? 'Delete' : 'Remove'}
        isLoading={isRemoving}
      />
    </Stack>
  );
};
